import React, { useEffect, useState } from 'react';
import { api } from '../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { MonitorPlay, Utensils, Beer, Settings2 } from 'lucide-react';
import { toast } from 'sonner';

const EventMenu = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Estado para a Refeição Dinâmica
  const [mealName, setMealName] = useState('Choripán');
  const [mealPrice, setMealPrice] = useState('15.00');

  // Estado para as Bebidas Selecionadas
  const [selectedProductIds, setSelectedProductIds] = useState([]);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await api.products.getAll();
      // Trazemos todos os produtos, mas focamos nas bebidas
      setProducts(response.data);
    } catch (error) {
      toast.error('Erro ao carregar os produtos do estoque.');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleProduct = (productId) => {
    setSelectedProductIds(prev => {
      if (prev.includes(productId)) {
        return prev.filter(id => id !== productId);
      } else {
        return [...prev, productId];
      }
    });
  };

  const handleOpenBoard = () => {
    if (!mealName || !mealPrice) {
      toast.error('Preencha o nome e o preço da refeição principal.');
      return;
    }

    if (selectedProductIds.length === 0) {
      toast.warning('Você não selecionou nenhuma bebida para o telão, mas vamos abrir assim mesmo.');
    }

    // Monta o pacote de dados para o Telão ler
    const selectedDrinks = products.filter(p => selectedProductIds.includes(p.id)).map(p => ({
      name: p.name,
      price: p.price
    }));

    const menuData = {
      meal: {
        name: mealName,
        price: parseFloat(mealPrice)
      },
      drinks: selectedDrinks
    };

    // Salva na memória do navegador
    localStorage.setItem('cabrones_event_menu', JSON.stringify(menuData));

    // Abre a rota do telão em uma nova aba (que vamos criar a seguir)
    window.open('/menu-board', '_blank');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <p className="text-muted-foreground font-mono">Carregando estoque...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="event-menu-config-page">
      <div className="flex items-center justify-between">
        <h1 className="text-5xl font-bold uppercase tracking-tight">Cardápio do Evento</h1>
        <Button 
          onClick={handleOpenBoard}
          className="bg-primary hover:bg-primary/90 text-primary-foreground font-bold uppercase h-12 px-6"
        >
          <MonitorPlay className="mr-2 h-5 w-5" /> Projetar Telão
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* COLUNA 1: CONFIGURAÇÃO DA COMIDA */}
        <div className="lg:col-span-1 space-y-6">
          <Card className="bg-card border-border">
            <CardHeader className="border-b border-border/50 pb-4">
              <CardTitle className="text-xl uppercase tracking-wide flex items-center gap-2">
                <Utensils className="h-5 w-5 text-primary" /> Refeição do Dia
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6 space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-wider">Nome do Prato</Label>
                <Input 
                  value={mealName}
                  onChange={(e) => setMealName(e.target.value)}
                  placeholder="Ex: Hambúrguer Artesanal"
                  className="mt-1 font-mono text-lg"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Preço de Venda (R$)</Label>
                <Input 
                  type="number"
                  step="0.01"
                  value={mealPrice}
                  onChange={(e) => setMealPrice(e.target.value)}
                  placeholder="Ex: 25.00"
                  className="mt-1 font-mono text-lg text-primary font-bold"
                />
              </div>
              <div className="bg-secondary/50 p-3 rounded-md border border-border mt-4">
                <p className="text-xs text-muted-foreground flex items-center gap-2">
                  <Settings2 className="h-4 w-4" /> 
                  Esta refeição não afeta o estoque geral. Ela serve apenas para exibição no telão do bar.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* COLUNA 2 e 3: SELEÇÃO DE BEBIDAS DO ESTOQUE */}
        <div className="lg:col-span-2 space-y-6">
          <Card className="bg-card border-border h-full">
            <CardHeader className="border-b border-border/50 pb-4 flex flex-row items-center justify-between">
              <CardTitle className="text-xl uppercase tracking-wide flex items-center gap-2">
                <Beer className="h-5 w-5 text-primary" /> Bebidas Disponíveis
              </CardTitle>
              <span className="text-xs font-mono text-muted-foreground uppercase">
                {selectedProductIds.length} selecionadas
              </span>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {products.map(product => {
                  const isSelected = selectedProductIds.includes(product.id);
                  return (
                    <div 
                      key={product.id}
                      onClick={() => handleToggleProduct(product.id)}
                      className={`flex items-center justify-between p-3 rounded-md cursor-pointer border transition-all ${
                        isSelected 
                          ? 'bg-primary/10 border-primary shadow-sm' 
                          : 'bg-secondary/20 border-border hover:border-primary/50'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-5 h-5 rounded flex items-center justify-center border ${isSelected ? 'bg-primary border-primary text-white' : 'border-muted-foreground bg-transparent'}`}>
                          {isSelected && <span className="text-sm font-bold">✓</span>}
                        </div>
                        <div>
                          <p className={`font-bold uppercase text-sm ${isSelected ? 'text-primary' : 'text-foreground'}`}>
                            {product.name}
                          </p>
                          <p className="text-xs text-muted-foreground font-mono">
                            Estoque: {product.stock}
                          </p>
                        </div>
                      </div>
                      <p className="font-mono font-bold">
                        R$ {product.price.toFixed(2)}
                      </p>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>

      </div>
    </div>
  );
};

export default EventMenu;
